import React, { useCallback, useEffect, useLayoutEffect, useState, } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import { AppView, Button, rem, Spacer } from 'design-system';
import { IconGoogleSheets } from 'design-system/icons';
import IconGoogle from 'design-system/icons/IconGoogle';
import IconRepeat from 'design-system/icons/IconRepeat';
import IconEmma from 'design-system/img/emma.png';
import Progress from 'design-system/Progress';
import Text from 'design-system/Text';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import { useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { postLiveSheetEnable, postLiveSheetSync, } from './actions';
import { useAuthorizeInGoogle } from './useAuthorizeInGoogle';
import { useLiveSheetUpdatedAtInterval } from './useLiveSheet';
import { useListenToPopupMessage } from './util';
const strings = {
    screenTitle: 'Live export',
    connectTitle: 'Connect to your Google account',
    connectDescription: [
        'Sign in with your Google account to link Google Sheets.',
        'Once connected, new transactions will be exported automatically in real time.',
    ],
    connectButtonLabel: 'Sign in with Google',
};
const LoadingView = () => {
    const colors = useColors();
    return (<View style={styles.flex}>
      <Progress style={styles.progress} color={colors.text.brand}/>
    </View>);
};
const ConnectView = ({ onPressConnect, loading }) => {
    const connectViewStyle = useMarginBottom('bottom', undefined, styles.connectView);
    return (<View style={connectViewStyle}>
      <View style={styles.flex}>
        <View style={styles.connectLogos}>
          <Image source={IconEmma}/>
          <IconRepeat colorKey="secondaryLight"/>
          <IconGoogleSheets width={64} height={64}/>
        </View>
        <Spacer h={40}/>
        <Text Text-24 OffBlack centered>
          {strings.connectTitle}
        </Text>
        <Spacer.H8 />
        {strings.connectDescription.map((para, index) => (<Text TextThin-16 centered Secondary key={index}>
            {para}
          </Text>))}
      </View>
      <Button thirdPartyOnDark title={strings.connectButtonLabel} leftIcon={<IconGoogle />} onPress={onPressConnect} loading={loading} preventDoubleTap/>
    </View>);
};
/**
 * This is the screen that the users see when they access the live export feature.
 * You should, however, skip this and navigate directly to `ExportDataLive`
 * if the feature is known to be enabled.
 *
 * This screen also automatically redirects to `ExportDataLive` if `isEnabled` and no `issue`.
 * In case `isEnabled` but issue exists in the backend, this will sync once before redirecting.
 *
 * Otherwise, this screen does what's necessary to let the user enable the feature.
 * Mainly it needs two steps:
 * 1. Authenticate with Google (if not already authenticated).
 * 2. POST to /enable endpoint.
 *
 * **Authentication**: See `useAuthorizeInGoogle`. It involves:
 * 1. Obtaining an "auth URL" from the backend;
 * 2. Opening it in an in-app browser or popup window;
 * 3. Proceed to step 2 to make the enabling request.
 *
 * **Enabling**: `enableIfAuthenticated` and the condition `!needAuthenticate` in
 * a fresh `liveSheetStatus` make this screen POST to /enable endpoint automatically
 * after authentication.
 */
const ExportDataLiveTransitScreen = ({ navigation, route, }) => {
    // If manageIfEnabled, this replaces the current route with ExportDataLiveScreen if
    // the status indicates that the feature is enabled.
    const { manageIfEnabled = false } = route.params ?? {};
    const [liveSheetStatus, refreshLiveSheetStatus] = useLiveSheetUpdatedAtInterval();
    const [enableIfAuthenticated, setEnableIfAuthenticated] = useState(route.params?.enableIfAuthenticated ?? false);
    useEffect(() => {
        refreshLiveSheetStatus();
    }, [route.params, refreshLiveSheetStatus]);
    useListenToPopupMessage(refreshLiveSheetStatus);
    const [doPostEnableResult, doPostEnableError, doingPostEnable, doPostEnable] = useFetchOld();
    const [doPostSyncResult, doPostSyncError, doingPostSync, doPostSync] = useFetchOld();
    const { authorizeInGoogle, isAuthorizing } = useAuthorizeInGoogle();
    const [isConnectBtnFrozen, setIsConnectBtnFrozen] = useState(false);
    useEffect(() => {
        let timeoutId;
        if (isConnectBtnFrozen) {
            timeoutId = setTimeout(() => {
                setIsConnectBtnFrozen(false);
            }, 3_000);
        }
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isConnectBtnFrozen]);
    const doingAnyRequest = doingPostEnable || doingPostSync || isAuthorizing;
    const hadAlreadyEnabledError = doPostEnableError?.apiPayload?.status === 409;
    useEffect(() => {
        if (liveSheetStatus === undefined || doingAnyRequest) {
            return;
        }
        // mapping behaviors to conditions
        let shouldEnable = false;
        let shouldSync = false;
        let shouldGoToManageScreen = false;
        let shouldGoToSuccessScreen = false;
        if (liveSheetStatus) {
            if (doPostEnableResult !== undefined ||
                hadAlreadyEnabledError ||
                doPostSyncResult !== undefined) {
                shouldGoToSuccessScreen = true;
            }
        }
        const shouldGoToRetryScreen = doPostEnableError !== undefined && !hadAlreadyEnabledError;
        const { isEnabled, needAuthenticate } = liveSheetStatus;
        if (enableIfAuthenticated && !needAuthenticate) {
            if (!isEnabled &&
                doPostEnableResult === undefined &&
                doPostEnableError === undefined) {
                shouldEnable = true;
            }
            if (isEnabled &&
                doPostEnableResult === undefined &&
                doPostEnableError === undefined &&
                doPostSyncResult === undefined &&
                doPostSyncError === undefined) {
                shouldSync = true;
            }
        }
        if (manageIfEnabled &&
            isEnabled &&
            doPostEnableResult === undefined &&
            doPostEnableError === undefined) {
            shouldGoToManageScreen = true;
        }
        if (shouldEnable) {
            doPostEnable(postLiveSheetEnable()).then(refreshLiveSheetStatus);
        }
        else if (shouldSync) {
            doPostSync(postLiveSheetSync()).then(refreshLiveSheetStatus);
        }
        else if (shouldGoToManageScreen) {
            navigation.replace('ExportDataLive');
        }
        else if (shouldGoToRetryScreen) {
            navigation.replace('ExportDataLiveRetry');
        }
        else if (shouldGoToSuccessScreen) {
            navigation.replace('ExportDataLiveSuccess');
        }
    }, [
        doPostEnable,
        doPostEnableError,
        doPostEnableResult,
        doPostSync,
        doPostSyncError,
        doPostSyncResult,
        doingAnyRequest,
        doingPostEnable,
        doingPostSync,
        enableIfAuthenticated,
        hadAlreadyEnabledError,
        liveSheetStatus,
        manageIfEnabled,
        refreshLiveSheetStatus,
    ]);
    // render
    const onPressConnect = useRequiredBenefitCallback(useCallback(() => {
        if (liveSheetStatus?.needAuthenticate === false) {
            doPostEnable(postLiveSheetEnable());
        }
        else {
            authorizeInGoogle();
            setIsConnectBtnFrozen(true);
            setEnableIfAuthenticated(true);
        }
    }, [authorizeInGoogle, doPostEnable, liveSheetStatus?.needAuthenticate]), 'export');
    const shouldRenderLoadingView = doingPostSync || doingPostEnable;
    if (shouldRenderLoadingView) {
        return (<AppView avoidHeader>
        <LoadingView />
      </AppView>);
    }
    return (<AppView avoidHeader withFrame>
      <ConnectView onPressConnect={onPressConnect} loading={doingAnyRequest || isConnectBtnFrozen}/>
    </AppView>);
};
const Wrapped = (props) => {
    const { navigation } = props;
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
            title: strings.screenTitle,
        });
    }, [colors.text.primary]);
    return <ExportDataLiveTransitScreen {...props}/>;
};
const styles = StyleSheet.create({
    flex: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: rem(4),
    },
    connectView: {
        flex: 1,
        marginHorizontal: rem(16),
    },
    connectLogos: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: rem(16),
    },
    progress: {
        width: rem(70),
        height: rem(70),
        marginBottom: rem(24),
    },
});
export default Wrapped;
